import type { HouseholdDetail, HouseholdRole, InvitationResponse } from '@recipes/contracts';
import {
  Alert,
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, LogOut, Mail, Trash2, UserMinus, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { FormField } from '@/features/shared/form-field';
import { formatDate, householdRoleLabel } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { ResponsiveTable } from '@/features/shared/responsive-table';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, toastError } from '@/lib/errors';
import { queryKeys } from '@/lib/query-keys';
import { toast } from '@/components/feedback/toaster';

type MemberRole = Exclude<HouseholdRole, 'OWNER'>;

export function HouseholdDetailPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const key = queryKeys.households.detail(id);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [removeUserId, setRemoveUserId] = useState<string | null>(null);
  const [transferUserId, setTransferUserId] = useState<string | null>(null);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [newName, setNewName] = useState('');

  const query = useQuery({
    queryKey: key,
    queryFn: () => api.households.get(id),
    enabled: Boolean(id),
  });
  const household = query.data;
  const isOwner = household?.myRole === 'OWNER';
  const setDetail = (d: HouseholdDetail) => {
    queryClient.setQueryData(key, d);
    void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
  };

  const updateRole = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: MemberRole }) =>
      api.households.updateMemberRole(id, userId, { role }),
    onSuccess: setDetail,
    onError: (e) => toastError(e),
  });
  const removeMember = useMutation({
    mutationFn: (userId: string) => api.households.removeMember(id, userId),
    onSuccess: (d) => {
      setDetail(d);
      setRemoveUserId(null);
    },
    onError: (e) => toastError(e),
  });
  const transfer = useMutation({
    mutationFn: (newOwnerUserId: string) =>
      api.households.transferOwnership(id, { newOwnerUserId }),
    onSuccess: (d) => {
      setDetail(d);
      setTransferUserId(null);
      toast.success(t('households.transferDone'));
    },
    onError: (e) => toastError(e),
  });
  const leave = useMutation({
    mutationFn: () => api.households.leave(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
      navigate('/households', { replace: true });
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: () => api.households.remove(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
      navigate('/households', { replace: true });
    },
    onError: (e) => toastError(e),
  });
  const rename = useMutation({
    mutationFn: () => api.households.update(id, { name: newName.trim() }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: key });
      void queryClient.invalidateQueries({ queryKey: queryKeys.households.all });
      setRenameOpen(false);
    },
    onError: (e) => toastError(e),
  });
  const revoke = useMutation({
    mutationFn: (invitationId: string) => api.households.revokeInvitation(id, invitationId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: key }),
    onError: (e) => toastError(e),
  });

  if (query.isLoading)
    return (
      <PageContainer>
        <Spinner label={t('app.loading')} />
      </PageContainer>
    );
  if (!household)
    return (
      <PageContainer>
        <Alert variant="destructive">{describeError(query.error)}</Alert>
      </PageContainer>
    );

  const transferCandidate = household.members.find((m) => m.user.id === transferUserId);

  return (
    <PageContainer className="max-w-4xl">
      <FeaturePageHeader
        title={household.name}
        description={t('households.memberCount', { count: household.memberCount })}
        backTo="/households"
        actions={
          <>
            {(isOwner || household.myRole === 'EDITOR') && (
              <Button onClick={() => setInviteOpen(true)} data-testid="household-invite">
                <UserPlus aria-hidden /> {t('households.invite')}
              </Button>
            )}
            {isOwner && (
              <Button
                variant="outline"
                onClick={() => {
                  setNewName(household.name);
                  setRenameOpen(true);
                }}
              >
                {t('households.rename')}
              </Button>
            )}
            {!isOwner && (
              <Button variant="outline" onClick={() => setLeaveOpen(true)}>
                <LogOut aria-hidden /> {t('households.leave')}
              </Button>
            )}
            {isOwner && (
              <Button variant="destructive" onClick={() => setDeleteOpen(true)}>
                <Trash2 aria-hidden /> {t('common.delete')}
              </Button>
            )}
          </>
        }
      />

      <section aria-labelledby="members-title" className="mb-8">
        <h2 id="members-title" className="mb-2 text-lg font-semibold">
          {t('households.members')}
        </h2>
        <ResponsiveTable
          caption={t('households.members')}
          rowKey={(m) => m.user.id}
          rows={household.members}
          columns={[
            {
              key: 'name',
              header: t('households.table.member'),
              cell: (m) => (
                <span className="flex items-center gap-2">
                  {m.user.avatarUrl ? (
                    <img
                      src={m.user.avatarUrl}
                      alt=""
                      className="size-8 rounded-full object-cover"
                    />
                  ) : (
                    <span className="size-8 rounded-full bg-muted" aria-hidden />
                  )}
                  <span className="font-medium">
                    {m.user.displayName}
                    {m.user.id === user?.id && (
                      <span className="ml-1 text-xs text-muted-foreground">
                        ({t('households.you')})
                      </span>
                    )}
                  </span>
                </span>
              ),
            },
            {
              key: 'role',
              header: t('households.table.role'),
              cell: (m) =>
                isOwner && m.role !== 'OWNER' ? (
                  <Select
                    value={m.role}
                    onValueChange={(v) =>
                      updateRole.mutate({ userId: m.user.id, role: v as MemberRole })
                    }
                  >
                    <SelectTrigger
                      aria-label={t('households.changeRole', { name: m.user.displayName })}
                      className="w-36"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="EDITOR">{householdRoleLabel(t, 'EDITOR')}</SelectItem>
                      <SelectItem value="VIEWER">{householdRoleLabel(t, 'VIEWER')}</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Badge variant={m.role === 'OWNER' ? 'default' : 'outline'}>
                    {householdRoleLabel(t, m.role)}
                  </Badge>
                ),
            },
            {
              key: 'joined',
              header: t('households.table.joined'),
              cell: (m) => formatDate(m.joinedAt, i18n.language),
            },
            {
              key: 'actions',
              header: '',
              label: t('households.table.actions'),
              cell: (m) =>
                isOwner && m.role !== 'OWNER' ? (
                  <span className="flex flex-wrap justify-end gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setTransferUserId(m.user.id)}
                    >
                      {t('households.transfer')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label={t('households.removeMember', { name: m.user.displayName })}
                      onClick={() => setRemoveUserId(m.user.id)}
                    >
                      <UserMinus aria-hidden />
                    </Button>
                  </span>
                ) : null,
            },
          ]}
        />
      </section>

      {household.pendingInvitations.length > 0 && (
        <section aria-labelledby="invitations-title">
          <h2 id="invitations-title" className="mb-2 text-lg font-semibold">
            {t('households.pendingInvitations')}
          </h2>
          <ResponsiveTable
            caption={t('households.pendingInvitations')}
            rowKey={(i) => i.id}
            rows={household.pendingInvitations}
            columns={[
              { key: 'email', header: t('auth.email'), cell: (i) => i.email },
              {
                key: 'role',
                header: t('households.table.role'),
                cell: (i) => householdRoleLabel(t, i.role),
              },
              {
                key: 'expires',
                header: t('households.table.expires'),
                cell: (i) => formatDate(i.expiresAt, i18n.language),
              },
              {
                key: 'actions',
                header: '',
                label: t('households.table.actions'),
                cell: (i) =>
                  isOwner || household.myRole === 'EDITOR' ? (
                    <Button size="sm" variant="ghost" onClick={() => revoke.mutate(i.id)}>
                      {t('households.revoke')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </section>
      )}

      <InviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        householdId={id}
        onInvited={() => void queryClient.invalidateQueries({ queryKey: key })}
      />
      <ConfirmDialog
        open={Boolean(removeUserId)}
        onOpenChange={(o) => !o && setRemoveUserId(null)}
        title={t('households.removeTitle')}
        destructive
        confirmLabel={t('households.removeConfirm')}
        loading={removeMember.isPending}
        onConfirm={() => {
          if (removeUserId) removeMember.mutate(removeUserId);
        }}
      />
      <ConfirmDialog
        open={Boolean(transferUserId)}
        onOpenChange={(o) => !o && setTransferUserId(null)}
        title={t('households.transferTitle')}
        description={t('households.transferBody', {
          name: transferCandidate?.user.displayName ?? '',
        })}
        confirmLabel={t('households.transfer')}
        loading={transfer.isPending}
        onConfirm={() => {
          if (transferUserId) transfer.mutate(transferUserId);
        }}
      />
      <ConfirmDialog
        open={leaveOpen}
        onOpenChange={setLeaveOpen}
        title={t('households.leaveTitle')}
        description={t('households.leaveBody')}
        destructive
        confirmLabel={t('households.leave')}
        loading={leave.isPending}
        onConfirm={() => leave.mutate()}
      />
      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t('households.deleteTitle')}
        description={t('households.deleteBody')}
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => remove.mutate()}
      />
      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent closeLabel={t('common.close')}>
          <DialogHeader>
            <DialogTitle>{t('households.rename')}</DialogTitle>
          </DialogHeader>
          <form
            noValidate
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (newName.trim()) rename.mutate();
            }}
          >
            <FormField label={t('households.fields.name')} required>
              <Input value={newName} maxLength={120} onChange={(e) => setNewName(e.target.value)} />
            </FormField>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setRenameOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" loading={rename.isPending} disabled={!newName.trim()}>
                {t('common.save')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}

function InviteDialog({
  open,
  onOpenChange,
  householdId,
  onInvited,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  householdId: string;
  onInvited: () => void;
}) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('EDITOR');
  const [result, setResult] = useState<InvitationResponse | null>(null);
  const invite = useMutation({
    mutationFn: () => api.households.invite(householdId, { email: email.trim(), role }),
    onSuccess: (res) => {
      setResult(res);
      onInvited();
      if (!res.inviteUrl) toast.success(t('households.inviteSent', { email }));
    },
    onError: (e) => toastError(e),
  });
  const close = (o: boolean) => {
    if (!o) {
      setResult(null);
      setEmail('');
    }
    onOpenChange(o);
  };
  const copy = async () => {
    if (!result?.inviteUrl) return;
    try {
      await navigator.clipboard.writeText(result.inviteUrl);
      toast.success(t('households.copied'));
    } catch {
      toast.error(t('households.copyFailed'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent closeLabel={t('common.close')}>
        <DialogHeader>
          <DialogTitle>{t('households.invite')}</DialogTitle>
          <DialogDescription>{t('households.inviteDescription')}</DialogDescription>
        </DialogHeader>
        {result ? (
          <div className="space-y-3">
            {result.inviteUrl ? (
              <>
                <Alert variant="info" title={t('households.inviteLinkTitle')}>
                  {t('households.inviteLinkBody')}
                </Alert>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={result.inviteUrl}
                    aria-label={t('households.inviteLinkTitle')}
                    onFocus={(e) => e.target.select()}
                    data-testid="invite-url"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void copy()}
                    aria-label={t('households.copy')}
                  >
                    <Copy aria-hidden />
                  </Button>
                </div>
              </>
            ) : (
              <Alert variant="success">
                <Mail className="size-4" aria-hidden /> {t('households.inviteSent', { email })}
              </Alert>
            )}
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>
                {t('common.close')}
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <form
            noValidate
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim()) invite.mutate();
            }}
          >
            <FormField label={t('auth.email')} required>
              <Input
                type="email"
                inputMode="email"
                autoComplete="off"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="invite-email"
              />
            </FormField>
            <div>
              <Label htmlFor="invite-role">{t('households.table.role')}</Label>
              <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
                <SelectTrigger id="invite-role" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EDITOR">{householdRoleLabel(t, 'EDITOR')}</SelectItem>
                  <SelectItem value="VIEWER">{householdRoleLabel(t, 'VIEWER')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                loading={invite.isPending}
                disabled={!email.trim()}
                data-testid="invite-submit"
              >
                {t('households.sendInvite')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
